import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { useModalContext } from '../../../models';
import { EditAutomationTemplateStepsContext, IEditAutomationTemplateStepsContext } from '../../../models/Automations';
import { useLambda } from '../../../models/hooks/useLambda';
import textingCampaignAutomationTriggerGraphic from '../../assets/texting-campaign-automation-trigger-graphic.svg';
import { Button } from '../../components-v2/Button';
import { asModalComponent } from '../../components/Modal';
import {
	AutomationOpportunityCreatedTriggerSettings,
	AutomationTagTriggerSettings,
	AutomationTriggerSettings,
} from '../../components/automation/AutomationTriggerCard/presentation';
import { BirthdayAutomationTriggerGraphic } from '../../components/svgs/graphics/BirthdayAutomationTriggerGraphic';
import { EventRegistrationTriggerGraphic } from '../../components/svgs/graphics/EventRegistrationTriggerGraphic';
import { ManualAutomationTriggerGraphic } from '../../components/svgs/graphics/ManualAutomationTriggerGraphic';
import { MeetingReminderTriggerGraphic } from '../../components/svgs/graphics/MeetingReminderTriggerGraphic';
import { NewClientAutomationTriggerGraphic } from '../../components/svgs/graphics/NewClientAutomationTriggerGraphic';
import { NewCommercialClientAutomationTriggerGraphic } from '../../components/svgs/graphics/NewCommercialClientAutomationTriggerGraphic';
import { NewDonorAutomationTriggerGraphic } from '../../components/svgs/graphics/NewDonorAutomationGraphic';
import { NewLeadsAutomationTriggerGraphic } from '../../components/svgs/graphics/NewLeadsAutomationTriggerGraphic';
import { NewPersonalClientAutomationTriggerGraphic } from '../../components/svgs/graphics/NewPersonalClientAutomationTriggerGraphic';
import { NewPolicyAutomationTriggerGraphic } from '../../components/svgs/graphics/NewPolicyAutomationGraphic';
import { RenewalAutomationTriggerGraphic } from '../../components/svgs/graphics/RenewalAutomationTriggerGraphic';
import { TagAutomationTriggerGraphic } from '../../components/svgs/graphics/TagAutomationTriggerGraphic';
import { Turning65AutomationTriggerGraphic } from '../../components/svgs/graphics/Turning65AutomationTriggerGraphic';
import { TurningXXTriggerGraphic } from '../../components/svgs/graphics/TurningXXGraphic';
import { RESOURCE_SELECTOR_DISPLAY } from '../../../models/utils/automationDisplayConfig';
import { baseStyleSheet } from '../../styles/styles';
import { styleSheet } from './styles';

interface IProps extends React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
	styles?: StyleDeclarationValue[];
	trigger?: Api.IAutomationTrigger;
	triggerName: string;
}

export const NewAutomationTemplateCard: React.FC<IProps> = ({
	trigger,
	className = '',
	styles = [],
	triggerName,
	...restProps
}) => {
	let Graphic: JSX.Element | null = null;
	switch (trigger?._type) {
		case Api.AutomationTriggerType.Texting: {
			Graphic = <img src={textingCampaignAutomationTriggerGraphic} />;
			break;
		}
		case Api.AutomationTriggerType.Tag: {
			Graphic = <TagAutomationTriggerGraphic />;
			break;
		}
		case Api.AutomationTriggerType.NewLead: {
			Graphic = <NewLeadsAutomationTriggerGraphic />;
			break;
		}
		case Api.AutomationTriggerType.Meeting: {
			Graphic = <MeetingReminderTriggerGraphic />;
			break;
		}
		case Api.AutomationTriggerType.EventRegistration: {
			Graphic = <EventRegistrationTriggerGraphic />;
			break;
		}
		case Api.AutomationTriggerType.NewDonationTrigger:
		case Api.AutomationTriggerType.NewDonor: {
			Graphic = <NewDonorAutomationTriggerGraphic />;
			break;
		}
		case Api.AutomationTriggerType.NewPolicyTrigger: {
			Graphic = <NewPolicyAutomationTriggerGraphic />;
			break;
		}
		case Api.AutomationTriggerType.NewMatterTrigger:
		case Api.AutomationTriggerType.MatterStageChangedTrigger:
		case Api.AutomationTriggerType.MatterStatusChangedTrigger: {
			// Use generic graphic for Matter triggers
			Graphic = <ManualAutomationTriggerGraphic />;
			break;
		}
		case Api.AutomationTriggerType.OpportunityCreated: {
			// Single tile — user picks the board inside the setup modal.
			Graphic = <NewLeadsAutomationTriggerGraphic />;
			break;
		}
		case Api.AutomationTriggerType.NewClient: {
			const t = trigger as Api.INewClientAutomationTrigger;
			if (t.clientType?.[0] === Api.NewClientType.Any) {
				Graphic = <NewClientAutomationTriggerGraphic />;
			} else if (t.clientType?.[0] === Api.NewClientType.Commercial) {
				Graphic = <NewCommercialClientAutomationTriggerGraphic />;
			} else if (t.clientType?.[0] === Api.NewClientType.Personal) {
				Graphic = <NewPersonalClientAutomationTriggerGraphic />;
			}
			break;
		}
		case Api.AutomationTriggerType.ResourceSelector: {
			const t = trigger as Api.IResourceSelectorAutomationTrigger;

			switch (t.resourceSelector) {
				case Api.ResourceSelectorId.HappyBirthday: {
					Graphic = <BirthdayAutomationTriggerGraphic />;
					break;
				}
				case Api.ResourceSelectorId.NewPolicy: {
					Graphic = <NewClientAutomationTriggerGraphic />;
					break;
				}
				case Api.ResourceSelectorId.PolicyRenew:
				case Api.ResourceSelectorId.FinancialReview: {
					Graphic = <RenewalAutomationTriggerGraphic />;
					break;
				}
				case Api.ResourceSelectorId.Turning72:
				case Api.ResourceSelectorId.Turning73:
				case Api.ResourceSelectorId.Turning65: {
					Graphic = <Turning65AutomationTriggerGraphic />;
					break;
				}
				case Api.ResourceSelectorId.Custom1:
				case Api.ResourceSelectorId.Custom2:
				case Api.ResourceSelectorId.Custom3:
				case Api.ResourceSelectorId.Custom4:
				case Api.ResourceSelectorId.Custom5:
				case Api.ResourceSelectorId.Custom6:
				case Api.ResourceSelectorId.Custom7:
				case Api.ResourceSelectorId.Custom8:
				case Api.ResourceSelectorId.Custom9:
				case Api.ResourceSelectorId.Custom10: {
					Graphic = <MeetingReminderTriggerGraphic />;
					break;
				}
				case Api.ResourceSelectorId.TurningXX:
				case Api.ResourceSelectorId.TurningXX2:
				case Api.ResourceSelectorId.TurningXX3:
				case Api.ResourceSelectorId.TurningXX4:
				case Api.ResourceSelectorId.TurningXX5: {
					Graphic = <TurningXXTriggerGraphic />;
					break;
				}
				default: {
					break;
				}
			}
			break;
		}
		default: {
			break;
		}
	}

	if (!Graphic) {
		Graphic = <ManualAutomationTriggerGraphic />;
	}
	const resourceSelectorId =
		trigger?._type === Api.AutomationTriggerType.ResourceSelector ||
		trigger?._type === Api.AutomationTriggerType.NewPolicyTrigger
			? (trigger as Api.IResourceSelectorAutomationTrigger).resourceSelector
			: undefined;
	const configLabel = resourceSelectorId ? RESOURCE_SELECTOR_DISPLAY[resourceSelectorId]?.label : undefined;
	const cardTitle = resourceSelectorId
		? `${configLabel ?? triggerName} Automation`
		: Api.VmUtils.Automations.triggers.getAutomationTriggerTitle(trigger);
	return (
		<button {...restProps} className={`${className} ${css(styleSheet.card, ...styles)}`}>
			<figure tabIndex={-1}>{Graphic}</figure>
			<span tabIndex={-1}>{cardTitle}</span>
		</button>
	);
};

interface IAutomationTemplateSetupSettings<T extends Api.IAutomationTrigger = Api.IAutomationTrigger> {
	impersonationContext?: Api.IImpersonationContext;
	trigger: T;
}

export const NewTagAutomationTemplateSetupSettings: React.FC<
	IAutomationTemplateSetupSettings<Api.ITagAutomationTrigger>
> = props => {
	const { impersonationContext, trigger } = props;
	const { parentModal } = useModalContext<Api.ITagAutomationTrigger>();

	const [triggerToSet, setTriggerToSet] = useLambda<Api.ITagAutomationTrigger>(trigger);

	const onCreateClicked = React.useCallback(() => {
		parentModal?.onRequestClose(triggerToSet, false);
	}, [triggerToSet, parentModal]);

	const onCancelClicked = React.useCallback(() => {
		parentModal?.onRequestClose(null, true);
	}, [parentModal]);

	const onRequestSetTrigger = React.useCallback(
		(t: Api.ITagAutomationTrigger) => {
			setTriggerToSet(t);
			return Promise.resolve();
		},
		[setTriggerToSet]
	);

	const stepsContext = React.useMemo<IEditAutomationTemplateStepsContext>(
		() => ({
			automationTemplateId: undefined,
			impersonationContext,
		}),
		[impersonationContext]
	);

	return (
		<EditAutomationTemplateStepsContext.Provider value={stepsContext}>
			<div className={css(styleSheet.triggerSettings)}>
				<AutomationTagTriggerSettings
					automationTemplate={undefined}
					impersonationContext={impersonationContext}
					onRequestSetTrigger={onRequestSetTrigger}
					trigger={triggerToSet}
				/>
				<div className={css(baseStyleSheet.horizontalStack)}>
					<button className={css(baseStyleSheet.ctaButton)} disabled={!triggerToSet?.tag} onClick={onCreateClicked}>
						<span>Create Automation</span>
					</button>
					<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={onCancelClicked}>
						<span>Cancel</span>
					</button>
				</div>
			</div>
		</EditAutomationTemplateStepsContext.Provider>
	);
};

export const NewTagAutomationTemplateSetupSettingsModal = asModalComponent(NewTagAutomationTemplateSetupSettings, {
	className: css(styleSheet.triggerSettingsModal),
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: true,
});

export const NewLeadAutomationTemplateSetupSettings: React.FC<
	IAutomationTemplateSetupSettings<Api.INewLeadAutomationTrigger>
> = props => {
	const { impersonationContext, trigger } = props;
	const { parentModal } = useModalContext<Api.INewLeadAutomationTrigger>();
	const [triggerToSet, setTriggerToSet] = useLambda<Api.INewLeadAutomationTrigger>(trigger);

	const onCreateClicked = React.useCallback(() => {
		parentModal?.onRequestClose(triggerToSet, false);
	}, [triggerToSet, parentModal]);

	const onCancelClicked = React.useCallback(() => {
		parentModal?.onRequestClose(null, true);
	}, [parentModal]);

	const onRequestSetTrigger = React.useCallback((t: Api.INewLeadAutomationTrigger) => {
		setTriggerToSet(t);
		return Promise.resolve();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const stepsContext = React.useMemo<IEditAutomationTemplateStepsContext>(
		() => ({
			automationTemplateId: undefined,
			impersonationContext,
		}),
		[impersonationContext]
	);

	return (
		<EditAutomationTemplateStepsContext.Provider value={stepsContext}>
			<div className={css(styleSheet.triggerSettings)}>
				<AutomationTriggerSettings
					impersonationContext={impersonationContext}
					onRequestSetTrigger={onRequestSetTrigger}
					trigger={triggerToSet}
				/>
				<div className={css(baseStyleSheet.horizontalStack)}>
					<button
						className={css(baseStyleSheet.ctaButton)}
						disabled={!(triggerToSet?.emailScannerIds?.length > 0)}
						onClick={onCreateClicked}
					>
						<span>Create Automation</span>
					</button>
					<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={onCancelClicked}>
						<span>Cancel</span>
					</button>
				</div>
			</div>
		</EditAutomationTemplateStepsContext.Provider>
	);
};

export const NewLeadAutomationTemplateSetupSettingsModal = asModalComponent(NewLeadAutomationTemplateSetupSettings, {
	className: css(styleSheet.triggerSettingsModal),
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: true,
});

export const NewOpportunityCreatedAutomationTemplateSetupSettings: React.FC<
	IAutomationTemplateSetupSettings<Api.IOpportunityCreatedAutomationTrigger>
> = props => {
	const { impersonationContext, trigger } = props;
	const { parentModal } = useModalContext<Api.IOpportunityCreatedAutomationTrigger>();

	const [triggerToSet, setTriggerToSet] = useLambda<Api.IOpportunityCreatedAutomationTrigger>(trigger);

	const onCreateClicked = React.useCallback(() => {
		parentModal?.onRequestClose(triggerToSet, false);
	}, [triggerToSet, parentModal]);

	const onCancelClicked = React.useCallback(() => {
		parentModal?.onRequestClose(null, true);
	}, [parentModal]);

	const onRequestSetTrigger = React.useCallback(
		(t: Api.IOpportunityCreatedAutomationTrigger) => {
			setTriggerToSet(t);
			return Promise.resolve();
		},
		[setTriggerToSet]
	);

	const stepsContext = React.useMemo<IEditAutomationTemplateStepsContext>(
		() => ({
			automationTemplateId: undefined,
			impersonationContext,
		}),
		[impersonationContext]
	);

	return (
		<EditAutomationTemplateStepsContext.Provider value={stepsContext}>
			<div className={css(styleSheet.triggerSettings)}>
				<AutomationOpportunityCreatedTriggerSettings
					automationTemplate={undefined}
					impersonationContext={impersonationContext}
					onRequestSetTrigger={onRequestSetTrigger}
					trigger={triggerToSet}
				/>
				<div className={css(baseStyleSheet.horizontalStack)}>
					<Button variant='primary' disabled={!triggerToSet?.boardId} onClick={onCreateClicked}>
						Create Automation
					</Button>
					<Button variant='secondary' onClick={onCancelClicked}>
						Cancel
					</Button>
				</div>
			</div>
		</EditAutomationTemplateStepsContext.Provider>
	);
};

export const NewOpportunityCreatedAutomationTemplateSetupSettingsModal = asModalComponent(
	NewOpportunityCreatedAutomationTemplateSetupSettings,
	{
		className: css(styleSheet.triggerSettingsModal),
		shouldCloseOnOverlayClick: false,
		useDefaultHeader: true,
	}
);
